import { useMemo, useState } from 'react';
import {
  IconAlarm, IconAlertTriangle, IconBell, IconClipboardCheck, IconClockHour4, IconDots, IconFilterOff, IconInbox, IconLayoutSidebarRight,
  IconLock, IconPhone, IconPlus, IconSparkles, IconAddressBook, IconCalendarPlus, IconExternalLink, IconFileText, IconRefresh,
} from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import {
  KIND, KPI, TASKS, abbr, actType, actIcon, dueIcon, dueShort, groupByCustomer, inScope, kpiCount, pace, taskKey, teamStats, visibleTasks,
  type Period, type Scope, type SaleTask, type TaskKind,
} from './salesTodayData';
import { SalesTodayCompact } from './SalesTodayCompact';
import { SalesTodayDock, useSalesTodayDock } from './SalesTodayDock';
import s from './salesToday.module.css';
import { useT } from '@hana/i18n';
import './i18n';

// Mint-SalesToday (desktop 1536): đếm việc 7 ô · nhịp việc · (cả nhóm) · việc theo khách. Chỉ đọc: nút ghi báo toast "Chưa mở ghi ở bản này".
// Tiền không hiện ở màn này. Dữ liệu mẫu từ script board (salesToday.ts).

const PERIODS = ['7', '30', '90'] as const;

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function DueIcon({ due }: { due: string }) {
  const k = dueIcon(due);
  if (k === 'alarm') return <IconAlarm size={13} stroke={1.8} aria-hidden="true" />;
  if (k === 'clock') return <IconClockHour4 size={13} stroke={1.8} aria-hidden="true" />;
  if (k === 'warn') return <IconAlertTriangle size={13} stroke={1.8} aria-hidden="true" />;
  return null;
}

function ActIco({ k }: { k: ReturnType<typeof actIcon> }) {
  const p = { size: 16, stroke: 1.8, 'aria-hidden': true as const };
  if (k === 'open' || k === 'req') return <IconExternalLink {...p} />;
  if (k === 'refresh') return <IconRefresh {...p} />;
  if (k === 'renew') return <IconCalendarPlus {...p} />;
  if (k === 'contract') return <IconFileText {...p} />;
  if (k === 'call') return <IconPhone {...p} />;
  return null;
}

function SalesTodayDesktop() {
  const t = useT();
  const toast = useToast();
  const dock = useSalesTodayDock();
  const forced = readState();
  const state: ScreenState = forced ?? 'ready';
  const noperm = state === 'noperm';
  const ready = state === 'ready' || noperm;
  const [per, setPer] = useState<Period>('30');
  const [scopeSel, setScopeSel] = useState<Scope>('mine');
  const [saleSel, setSaleSel] = useState<string | null>(null);
  const [fk, setFk] = useState<TaskKind | null>(null);
  const [done, setDone] = useState<Record<string, boolean>>({});
  const scope: Scope = noperm ? 'mine' : scopeSel;

  const all = useMemo(() => (state === 'empty' ? [] : visibleTasks(TASKS, per)), [state, per]);
  const rows = useMemo(() => inScope(all, scope, saleSel), [all, scope, saleSel]);
  const shown = useMemo(() => rows.filter((x) => !fk || x.kind === fk), [rows, fk]);
  const groups = useMemo(() => groupByCustomer(shown, done), [shown, done]);
  const p = pace(rows, done);
  const team = useMemo(() => teamStats(all, done), [all, done]);
  const noWrite = () => toast({ msg: t('crm.noWrite') });
  const open = (x: SaleTask) => toast({ msg: t('crm.st.openLater', { v: x.act.toLowerCase() }) });
  const count = (sc: Scope) => inScope(all, sc, null).filter((x) => !done[taskKey(x)]).length;

  const empty = state === 'empty';
  const fkLabel = fk ? KIND[fk].label : t('crm.st.byCust');

  return (
    <Page kind="table" dock={dock.wide && ready ? <SalesTodayDock dock={dock} /> : undefined}>
      <PageHeader
        title={t('crm.st.title')}
        meta={<span className={s.kind + ' ' + s.pri}>{t('crm.st.date')}</span>}
        tabs={
          <>
            <Segmented<Scope>
              label={t('crm.st.scope')}
              value={scope}
              onChange={(k) => { setScopeSel(k); setSaleSel(null); }}
              items={noperm ? [{ key: 'mine', label: t('crm.st.mine'), n: count('mine') }] : [{ key: 'mine', label: t('crm.st.mine'), n: count('mine') }, { key: 'team', label: t('crm.st.team'), n: count('team') }]}
            />
            <div style={{ marginLeft: 'auto' }}>
              <Segmented<Period> label={t('crm.st.period')} kind="toggle" value={per} onChange={setPer} items={PERIODS.map((x) => ({ key: x, label: t('crm.st.days', { n: x }) }))} />
            </div>
          </>
        }
        actions={
          <>
            <HeaderTool label={t('crm.st.noti')} icon={IconBell} n={3} onClick={() => dock.show('noti')} />
            <HeaderTool label={t('crm.st.openDock')} icon={IconLayoutSidebarRight} active={dock.open} onClick={() => dock.toggle('note')} />
            <HeaderTool label={t('crm.st.more')} icon={IconDots} onClick={noWrite} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
              {t('crm.st.add')}
            </Button>
          </>
        }
      />
      <div className={s.page}>
        <StateView
          state={ready && !empty ? 'ready' : empty ? 'empty' : state}
          title={empty ? t('crm.st.emptyTitle') : state === 'error' ? t('crm.st.errTitle') : undefined}
          desc={empty ? t('crm.st.emptyDesc') : state === 'error' ? t('crm.st.errDesc') : undefined}
          icon={empty ? <IconInbox size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={noWrite}>{t('crm.retry')}</Button> : empty ? <Button onClick={noWrite}>{t('crm.st.add')}</Button> : undefined}
          skeleton={<Skeleton rows={8} />}
        >
          {noperm && (
            <div role="note" className={s.note}>
              <IconLock size={16} aria-hidden="true" /> {t('crm.st.onlyMine')}
            </div>
          )}
          <section aria-label={t('crm.st.kpi')} className={s.kpis}>
            {KPI.map((k) => {
              const c = kpiCount(rows, k.kind, done);
              return (
                <button key={k.kind} type="button" className={s.kpi} aria-pressed={fk === k.kind} onClick={() => setFk(fk === k.kind ? null : k.kind)}>
                  <span>{k.label}</span>
                  <b className={c.overdue ? s.od : undefined}>{c.n}</b>
                </button>
              );
            })}
          </section>
          <section aria-label={t('crm.st.pace')} className={s.pace}>
            <div>
              <div className={s.paceHead}>
                <b>{t('crm.st.byDue')}</b>
                <span className={s.sub}>{p.sub}</span>
              </div>
              <div className={s.bar} role="img" aria-label={`${t('crm.st.byDue')}: ${p.segs.map((x) => `${x.k} ${x.n}`).join(', ')}`}>
                {p.segs.filter((x) => x.n > 0).map((x) => (
                  <div key={x.k} title={`${x.k} ${x.n}`} style={{ flex: `${x.n} 1 0`, background: x.c }} />
                ))}
              </div>
              <div className={s.lg}>
                {p.segs.map((x) => (
                  <span key={x.k}>
                    <i style={{ background: x.c }} />
                    {x.k} <b>{x.n}</b>
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className={s.paceHead}>
                <b>{t('crm.st.doneIn')}</b>
                <span>
                  <span className={s.big}>{p.pct}%</span> <span className={s.sub}>{t('crm.st.ofTasks', { done: p.done, all: p.all })}</span>
                </span>
              </div>
              <div className={s.ticks} role="img" aria-label={t('crm.st.doneAria', { done: p.done, all: p.all })}>
                {Array.from({ length: 24 }, (_, i) => (
                  <div key={i} className={i < p.ticksOn ? s.on : undefined} />
                ))}
              </div>
            </div>
          </section>
          {scope === 'team' && (
            <section aria-label={t('crm.st.teamTasks')} className={s.team}>
              <div className={s.teamHead}>
                <b>{t('crm.st.team')}</b>
                <span>{t('crm.st.teamHint')}</span>
              </div>
              <div className={s.teamGrid}>
                {team.map((m) => (
                  <button key={m.k} type="button" className={s.tm} aria-pressed={saleSel === m.k} onClick={() => setSaleSel(saleSel === m.k ? null : m.k)}>
                    <span className={s.tmAb}>{m.ab}</span>
                    <span className={s.tmBody}>
                      <b>{m.k}</b>
                      <span>{t('crm.st.tmLine', { open: m.open, n: m.customers })}</span>
                      <span className={s.tmBar} role="img" aria-label={t('crm.st.tmAria', { k: m.k, open: m.open, od: m.od })}>
                        {[['var(--err)', m.segs[0]!], ['var(--pri)', m.segs[1]!], ['transparent', m.segs[2]!]].filter(([, n]) => (n as number) > 0).map(([c, n]) => (
                          <span key={c as string} style={{ flex: `${n} 1 0`, background: c as string }} />
                        ))}
                      </span>
                    </span>
                    <b>{m.od ? t('crm.st.overdueN', { n: m.od }) : ''}</b>
                  </button>
                ))}
              </div>
            </section>
          )}
          <div className={s.listHead}>
            <b>{fkLabel}</b>
            <span>{t('crm.st.listCount', { c: groups.length, n: shown.filter((x) => !done[taskKey(x)]).length })}</span>
            {fk && (
              <button type="button" className={s.clear} onClick={() => setFk(null)}>
                <IconFilterOff size={16} stroke={1.8} aria-hidden="true" />
                {t('crm.st.clear')}
              </button>
            )}
          </div>
          {groups.length === 0 && <div className={s.none}>{t('crm.st.none')}</div>}
          {groups.map((g) => (
            <section key={g.sn} aria-label={t('crm.st.tasksOf', { v: g.sn })} className={s.grp}>
              <div className={s.gh}>
                <span className={s.gAb}>{g.sn.slice(0, 2)}</span>
                <div className={s.gMain}>
                  <a className={s.gName} href={`/ezy/customers/${g.sn}`}>{g.sn}</a>
                  <div className={s.gMeta}>
                    <span title={t('crm.st.contact', { v: g.contact })}>
                      <IconAddressBook size={13} stroke={1.8} aria-hidden="true" />
                      {g.contact}
                    </span>
                    <span title={t('crm.ct.sale', { v: g.sale })}>
                      <span className={s.ab} aria-hidden="true">{abbr(g.sale)}</span>
                      {g.sale}
                    </span>
                  </div>
                </div>
                <span className={`${s.gOd} ${g.overdue ? s.hot : ''}`} title={g.overdue ? t('crm.st.overdueN', { n: g.overdue }) : t('crm.st.nTasks', { n: g.open })}>
                  {g.overdue ? <IconAlertTriangle size={13} stroke={1.8} aria-hidden="true" /> : <IconClipboardCheck size={13} stroke={1.8} aria-hidden="true" />}
                  {g.overdue || g.open}
                </span>
                <button type="button" className={s.ib} aria-label={t('crm.st.callAria', { v: g.sn })} title={t('crm.st.callAria', { v: g.sn })} onClick={() => toast({ msg: t('crm.st.calling', { v: g.contact }) })}>
                  <IconPhone size={15} stroke={1.9} aria-hidden="true" />
                </button>
                <button type="button" className={s.ib} aria-label={t('crm.st.log')} title={t('crm.st.log')} onClick={noWrite}>
                  <IconClipboardCheck size={16} stroke={1.8} aria-hidden="true" />
                </button>
              </div>
              {g.rows.map((r) => {
                const k = taskKey(r);
                const isDone = !!done[k];
                const at = actType(r);
                return (
                  <div key={k} className={`${s.row} ${isDone ? s.done : ''}`}>
                    <input type="checkbox" aria-label={t('crm.st.doneTask', { v: r.text })} checked={isDone} onChange={() => setDone({ ...done, [k]: !isDone })} />
                    <span>
                      <span className={`${s.kind} ${s[KIND[r.kind].tone]}`}>{KIND[r.kind].label}</span>
                    </span>
                    <span className={s.tx}>{r.text}</span>
                    <span className={`${s.due} ${r.lvl < 0 ? s.over : r.lvl === 0 ? s.today : ''}`} title={r.due}>
                      <DueIcon due={r.due} />
                      <span className={s.tx}>{dueShort(r.due)}</span>
                    </span>
                    <span className={s.act}>
                      {at === 'ai' ? (
                        <button type="button" className={`${s.go} ${s.ai}`} onClick={noWrite}>
                          <IconSparkles size={13} stroke={1.9} aria-hidden="true" />
                          {r.act}
                        </button>
                      ) : (
                        <button type="button" className={s.go} onClick={() => (at === 'call' ? toast({ msg: t('crm.st.calling', { v: g.contact }) }) : open(r))}>
                          <ActIco k={actIcon(r.act)} />
                          {r.act}
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
            </section>
          ))}
        </StateView>
      </div>
    </Page>
  );
}

/** Máy bàn: Mint-SalesToday. iPad / điện thoại: Mint-SalesToday-iPad / -Phone. */
export function SalesToday() {
  const layout = useLayout();
  return layout === 'pad' || layout === 'phone' ? <SalesTodayCompact phone={layout === 'phone'} /> : <SalesTodayDesktop />;
}
